import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdminPage = location.pathname.startsWith('/admin');
  const isLightPage = location.pathname === '/registrieren' || 
                      location.pathname === '/kurszugang' || 
                      location.pathname === '/zertifikat' || 
                      location.pathname.startsWith('/verify/') ||
                      location.pathname === '/login' ||
                      location.pathname === '/datenschutz' ||
                      location.pathname === '/impressum' ||
                      location.pathname === '/agb' ||
                      location.pathname === '/reset-password' ||
                      location.pathname === '/faq' ||
                      location.pathname === '/invite-signup' ||
                      location.pathname === '/auth/callback' ||
                      location.pathname === '/test' ||
                      isAdminPage;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const scrollToSection = (sectionId: string) => {
    // First navigate to home if not already there
    if (location.pathname !== '/') {
      navigate('/');
      // Wait for navigation to complete
      setTimeout(() => {
        const section = document.getElementById(sectionId);
        if (section) {
          section.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  // Get current user from localStorage to check admin status
  const isUserAdmin = user?.isAdmin === true || user?.isSuperAdmin === true;

  return (
    <nav 
      className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled || isAdminPage ? 'bg-white shadow-md py-2' : 'bg-transparent py-4'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center">
          <Link to="/" className={`flex items-center ${isAdminPage ? 'invisible' : 'visible'}`}>
            <svg 
              width="32" 
              height="32" 
              viewBox="0 0 100 100" 
              className="text-[#EE7F01]"
            >
              <g transform="translate(0,-952.36216)">
                <path 
                  className="fill-current"
                  d="m 49.979083,957.36216 -25.142573,13.00244 0.02091,25.9726 25.397236,51.025 24.908919,-51.0579 -0.02099,-25.97231 -25.163495,-12.96983 z m 0.02091,3.96408 21.416994,11.05742 0,22.11483 -21.416994,11.05741 -21.416995,-11.05741 0,-22.11483 21.416995,-11.05742 z"
                />
                <path 
                  className="fill-current"
                  d="m 46.216046,981.37189 15.844743,-10.99793 -8.276708,15.13629 -15.844743,10.99793 z"
                />
              </g>
            </svg>
            <span className={`ml-2 font-semibold text-xl ${
              scrolled || isLightPage ? 'text-gray-800' : 'text-white'
            }`}>
              KI-Navigatoren
            </span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            <button 
              onClick={() => scrollToSection('about')}
              className={`${scrolled || isLightPage ? 'text-gray-700' : 'text-white'} hover:text-[#EE7F01] transition-colors`}
            >
              Über den Kurs
            </button>
            <button 
              onClick={() => scrollToSection('pricing')}
              className={`${scrolled || isLightPage ? 'text-gray-700' : 'text-white'} hover:text-[#EE7F01] transition-colors`}
            >
              Preise
            </button>
            
            {isAuthenticated ? (
              <div className="flex items-center space-x-4">
                <Link 
                  to="/kurszugang" 
                  className="bg-[#2574B5] hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                >
                  Mein Kurs
                </Link>
                {isUserAdmin && (
                  <Link 
                    to="/admin" 
                    className="bg-[#EE7F01] hover:bg-orange-600 text-white font-medium py-2 px-4 rounded-md transition-colors" 
                  >
                    Admin
                  </Link>
                )}
                <button 
                  onClick={logout}
                  className={`${scrolled || isLightPage ? 'text-gray-700' : 'text-white'} hover:text-[#EE7F01] transition-colors`}
                >
                  Abmelden
                </button>
              </div>
            ) : (
              <Link 
                to="/login" 
                className="bg-[#2574B5] hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
              >
                Login
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-gray-700" 
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? "Close Menu" : "Open Menu"}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden bg-white shadow-lg absolute w-full left-0 mt-2">
            <div className="container mx-auto px-4 py-4 flex flex-col space-y-4">
              <button 
                onClick={() => scrollToSection('about')}
                className="text-gray-700 hover:text-[#EE7F01] py-2"
              >
                Über den Kurs
              </button>
              <button 
                onClick={() => scrollToSection('pricing')}
                className="text-gray-700 hover:text-[#EE7F01] py-2"
              >
                Preise
              </button>
              
              {isAuthenticated ? (
                <>
                  <Link 
                    to="/kurszugang" 
                    className="bg-[#2574B5] hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors text-center"
                  >
                    Mein Kurs
                  </Link>
                  {isUserAdmin && (
                    <Link 
                      to="/admin" 
                      className="bg-[#EE7F01] hover:bg-orange-600 text-white font-medium py-2 px-4 rounded-md transition-colors text-center" 
                    >
                      Admin
                    </Link>
                  )}
                  <button 
                    className="text-gray-700 hover:text-[#EE7F01] py-2 flex items-center justify-center"
                    onClick={async () => {
                      await logout();
                      navigate('/login', { replace: true });
                    }}
                  >
                    Abmelden
                  </button>
                </>
              ) : (
                <Link 
                  to="/login" 
                  className="bg-[#2574B5] hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors text-center"
                >
                  Login
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;